<template>
  <div>
          <div class="card">
            <div class="card-body">
              <div class="row">
                <div class="col-md-8">
                  <h4 class="card-title">Sync Stripe to Postgres</h4>
                  <p class="card-text">
                    Pull the latest Stripe products, prices and features into the AhaSlides
                    Postgres mirror tables. This is the same job as the manual
                    <code>cli/stripe-product-and-price-sync.js</code> GitHub workflow, run on demand.
                  </p>

                  <div v-if="lastResult" class="alert alert-success">
                    <i class="fa fa-check-circle mr-1" aria-hidden="true"></i>
                    <strong>Last sync:</strong> synced
                    {{ lastResult.productsN }} products and {{ lastResult.pricesN }} prices
                    <span v-if="lastSyncTime">at {{ lastSyncTime }}</span>.
                  </div>
                </div>
                <div class="col-md-4 text-center">
                  <button
                    class="btn btn-primary btn-lg"
                    :disabled="isLoading"
                    @click="triggerStripeSync"
                  >
                    <i
                      class="fa fa-sync fa-fw"
                      aria-hidden="true"
                      :class="{ 'fa-spin': isLoading }"
                    ></i>
                    {{ isLoading ? 'Syncing...' : 'Sync now' }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Help Information -->
          <div class="card">
            <div class="card-body">
              <h5 class="card-title">
                <i class="fa fa-question-circle text-info" aria-hidden="true"></i> About this sync
              </h5>
              <p>
                Webhooks already mirror product and price <em>edits</em> automatically, but
                <strong>no webhook covers feature (entitlement) changes</strong>. Attaching, detaching and
                creating features on the Features and Products tabs mirror themselves. Run this after you change
                features directly in Stripe, or if that tab reports that the mirror was not updated. It:
              </p>
              <ul>
                <li>Runs the full-catalog mirror against the live Stripe account</li>
                <li>Upserts every product, price and feature into the Postgres mirror tables</li>
                <li>Is safe to click repeatedly (idempotent upsert)</li>
              </ul>
              <div class="alert alert-warning">
                <i class="fa fa-exclamation-triangle mr-1" aria-hidden="true"></i>
                <strong>Note:</strong> The sync runs synchronously and may take up to a minute for the
                full catalog. Wait for the success message. If another sync is already running you will
                be asked to try again in a moment.
              </div>
            </div>
          </div>
  </div>
</template>

<script>
import { isOpaqueError, withErrorRef } from '@/apiErrorRef'
import Config from '@/config'
import utils from '@/utils'
import { loadCatalog } from './catalogStore'

export default {
  name: 'StripeBridgeSync',
  data() {
    return {
      isLoading: false,
      lastResult: null,
      lastSyncTime: null,
    }
  },
  methods: {
    async triggerStripeSync() {
      if (this.isLoading) return

      this.isLoading = true

      try {
        // Admin JWT is attached globally from the ahaTokenAdmin cookie (see router.js).
        // The endpoint runs the mirror inline and returns { ok, productsN, pricesN }.
        const res = await this.$http.post(Config.api.syncStripeMirror)

        this.lastResult = res.body
        loadCatalog()
        this.lastSyncTime = utils.isoDateTime(new Date())
        utils.showToastSuccess(
          `Synced ${res.body.productsN} products and ${res.body.pricesN} prices into the mirror.`,
        )
      } catch (error) {
        // 409 = a sync is already in progress; anything else = generic failure.
        if (error.status === 409) {
          utils.showToastError('A sync is already running. Give it a moment and try again.')
        } else {
          const errorMessage =
            (error.body && (error.body.error || error.body.message)) ||
            (!isOpaqueError(error) && error.bodyText) ||
            withErrorRef(error, 'Could not sync Stripe to the mirror. Please try again.')
          utils.showToastError(errorMessage)
        }
      } finally {
        this.isLoading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.btn-lg {
  padding: 12px 24px;
  font-size: 16px;
}

.alert {
  border-radius: 8px;
}

.card-title {
  color: #2c3e50;
  font-weight: 600;
}

.card-text {
  color: #6c757d;
  font-size: 14px;
}
</style>
